import { useEffect, useState, Fragment } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import {
  Wheat, Printer, Plus, Pencil, Trash2, X, Check,
  ExternalLink, AlertTriangle, FileSpreadsheet, FileText,
} from "lucide-react";
import { toast } from "sonner";
import Header from "../components/Header";
import BottomNav from "../components/BottomNav";
import AIOtherCropsInput from "../components/AIOtherCropsInput";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtDate = (iso, locale = "it-IT") =>
  iso ? new Date(iso).toLocaleDateString(locale, { day: "2-digit", month: "2-digit", year: "numeric" }) : "—";

const toInputDate = (iso) => (iso ? iso.slice(0, 10) : "");

const EMPTY_FORM = {
  treatment_date: "", crop_name: "", product_name: "", quantity: "", quantity_unit: "", zone: "",
  surface_treated: "", target_disease: "", operator_name: "", notes: "",
};

// Stessa logica di Catalogo.jsx: nessun endpoint dedicato, apre una ricerca Google filtrata per PDF.
const cercaSDS = (nome) => {
  const query = `"${nome}" scheda sicurezza fitosanitario filetype:pdf`;
  window.open(`https://www.google.com/search?q=${encodeURIComponent(query)}`, "_blank");
};

export default function OtherCrops() {
  const { t, i18n } = useTranslation();
  const dateLocale = i18n.language === "fr" ? "fr-FR" : "it-IT";
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManualForm, setShowManualForm] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [downloading, setDownloading] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/other-crop-treatments`, { withCredentials: true });
      setItems(res.data || []);
    } catch {
      toast.error(t("otherCrops.loadError"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.treatment_date || !form.crop_name || !form.product_name || !form.quantity || !form.quantity_unit) return;
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        quantity: Number(form.quantity),
        surface_treated: form.surface_treated !== "" ? Number(form.surface_treated) : null,
      };
      const res = await axios.post(`${API}/other-crop-treatments`, payload, { withCredentials: true });
      setItems((prev) => [res.data, ...prev]);
      setForm(EMPTY_FORM);
      setShowManualForm(false);
      toast.success(t("otherCrops.createSuccess"));
    } catch {
      toast.error(t("otherCrops.createError"));
    } finally {
      setSubmitting(false);
    }
  };

  const openEdit = (item) => {
    setEditItem(item);
    setEditForm({
      treatment_date: toInputDate(item.treatment_date),
      crop_name: item.crop_name || "",
      product_name: item.product_name || "",
      quantity: item.quantity ?? "",
      quantity_unit: item.quantity_unit || "",
      zone: item.zone || "",
      surface_treated: item.surface_treated ?? "",
      target_disease: item.target_disease || "",
      operator_name: item.operator_name || "",
      notes: item.notes || "",
    });
  };

  const closeEdit = () => { setEditItem(null); setEditForm({}); };

  const saveEdit = async () => {
    setSaving(true);
    try {
      const payload = {
        ...editForm,
        quantity: editForm.quantity !== "" ? Number(editForm.quantity) : null,
        surface_treated: editForm.surface_treated !== "" ? Number(editForm.surface_treated) : null,
      };
      const res = await axios.put(`${API}/other-crop-treatments/${editItem.id}`, payload, { withCredentials: true });
      setItems((prev) => prev.map((x) => (x.id === editItem.id ? { ...x, ...res.data } : x)));
      toast.success(t("otherCrops.updateSuccess"));
      closeEdit();
    } catch {
      toast.error(t("otherCrops.updateError"));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(t("otherCrops.confirmDelete"))) return;
    try {
      await axios.delete(`${API}/other-crop-treatments/${id}`, { withCredentials: true });
      setItems((prev) => prev.filter((x) => x.id !== id));
      toast.success(t("otherCrops.deleteSuccess"));
    } catch {
      toast.error(t("otherCrops.deleteError"));
    }
  };

  const printExport = () => window.print();

  const sorted = [...items].sort((a, b) => new Date(b.treatment_date) - new Date(a.treatment_date));

  // Stessa logica di downloadFile in Journal.jsx, adattata agli endpoint /export/other-crops/*.
  const downloadFile = async (format) => {
    if (downloading) return;
    setDownloading(format);
    try {
      const ext = format === "excel" ? "xlsx" : "pdf";
      const res = await axios.get(`${API}/export/other-crops/${format}`, {
        withCredentials: true,
        responseType: "blob",
        timeout: 60000,
      });
      const blob = new Blob([res.data]);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `altre-colture-${new Date().toISOString().slice(0, 10)}.${ext}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(t("journal.downloadSuccess", { ext: ext.toUpperCase() }));
    } catch (e) {
      toast.error(t("journal.downloadError"));
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="min-h-screen pb-24" data-testid="other-crops-page">
      <Header title={t("otherCrops.pageTitle")} subtitle={t("otherCrops.pageSubtitle")} />
      <main className="max-w-7xl mx-auto px-4 py-5">
        <div className="flex items-center gap-2 mb-4 print-hide flex-wrap">
          <Wheat size={22} style={{ color: "var(--primary)" }} />
          <h2 className="heading text-3xl font-extrabold">{t("otherCrops.heading")}</h2>
          <div className="ml-auto flex items-center gap-2 flex-wrap">
            <button onClick={() => downloadFile("excel")} disabled={downloading === "excel"}
              className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: "white", color: "#166534", border: "2px solid #22c55e" }}
              data-testid="other-crops-download-excel-btn"
              title={t("otherCrops.excelButtonTitle")}>
              <FileSpreadsheet size={18} /> {downloading === "excel" ? "..." : t("otherCrops.excelButtonLabel")}
            </button>
            <button onClick={() => downloadFile("pdf")} disabled={downloading === "pdf"}
              className="inline-flex items-center gap-2 text-sm px-3 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
              style={{ background: "white", color: "#991b1b", border: "2px solid #dc2626" }}
              data-testid="other-crops-download-pdf-btn"
              title={t("otherCrops.pdfButtonTitle")}>
              <FileText size={18} /> {downloading === "pdf" ? "..." : t("otherCrops.pdfButtonLabel")}
            </button>
            <button onClick={printExport}
              className="inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105"
              style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)" }}
              data-testid="other-crops-print-btn">
              <Printer size={18} /> {t("otherCrops.printButtonLabel")}
            </button>
          </div>
        </div>

        <div className="print-hide">
          <AIOtherCropsInput onCreated={load} />
        </div>

        {!showManualForm ? (
          <button onClick={() => setShowManualForm(true)}
            className="vs-btn-secondary inline-flex items-center gap-2 text-sm mb-5 print-hide"
            data-testid="other-crops-show-manual-form-btn">
            <Plus size={16} /> {t("otherCrops.showManualFormButton")}
          </button>
        ) : (
        <form onSubmit={handleCreate} className="vs-card p-4 sm:p-5 mb-5 print-hide" data-testid="other-crops-add-form">
          <div className="flex items-center justify-between mb-3">
            <h3 className="heading font-extrabold text-base">{t("otherCrops.addFormTitle")}</h3>
            <button type="button" onClick={() => setShowManualForm(false)} className="p-1"
              aria-label={t("otherCrops.hideManualFormAriaLabel")} data-testid="other-crops-hide-manual-form-btn">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <FormField label={t("otherCrops.fields.date")} type="date" value={form.treatment_date} required
              onChange={(v) => setForm((f) => ({ ...f, treatment_date: v }))} />
            <FormField label={t("otherCrops.fields.crop")} value={form.crop_name} required
              placeholder={t("otherCrops.fields.cropPlaceholder")}
              onChange={(v) => setForm((f) => ({ ...f, crop_name: v }))} />
            <FormField label={t("otherCrops.fields.product")} value={form.product_name} required
              onChange={(v) => setForm((f) => ({ ...f, product_name: v }))} />
            <div className="grid grid-cols-2 gap-2 col-span-2 sm:col-span-1">
              <FormField label={t("otherCrops.fields.quantity")} type="number" value={form.quantity} required
                onChange={(v) => setForm((f) => ({ ...f, quantity: v }))} />
              <FormField label={t("otherCrops.fields.quantityUnit")} value={form.quantity_unit} required
                placeholder="kg, L..."
                onChange={(v) => setForm((f) => ({ ...f, quantity_unit: v }))} />
            </div>
            <FormField label={t("otherCrops.fields.zone")} value={form.zone}
              placeholder={t("otherCrops.fields.zonePlaceholder")}
              onChange={(v) => setForm((f) => ({ ...f, zone: v }))} />
            <FormField label={t("otherCrops.fields.surface")} type="number" value={form.surface_treated}
              onChange={(v) => setForm((f) => ({ ...f, surface_treated: v }))} />
            <FormField label={t("otherCrops.fields.disease")} value={form.target_disease}
              onChange={(v) => setForm((f) => ({ ...f, target_disease: v }))} />
            <FormField label={t("otherCrops.fields.operator")} value={form.operator_name}
              onChange={(v) => setForm((f) => ({ ...f, operator_name: v }))} />
            <div className="col-span-2 sm:col-span-4">
              <FormField label={t("otherCrops.fields.notes")} value={form.notes} textarea
                onChange={(v) => setForm((f) => ({ ...f, notes: v }))} />
            </div>
          </div>
          <button type="submit" disabled={submitting}
            className="mt-3 inline-flex items-center gap-2 text-sm px-4 py-2.5 rounded-xl font-bold shadow-sm transition-transform hover:scale-105 disabled:opacity-60"
            style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)" }}
            data-testid="other-crops-submit-btn">
            <Plus size={18} /> {submitting ? t("otherCrops.saving") : t("otherCrops.addButton")}
          </button>
        </form>
        )}

        {loading ? (
          <div className="flex justify-center py-10"><div className="vs-spinner" /></div>
        ) : sorted.length === 0 ? (
          <div className="vs-card p-8 text-center">
            <Wheat size={42} style={{ color: "var(--text-muted)" }} className="mx-auto mb-3" />
            <p className="font-semibold">{t("otherCrops.empty")}</p>
            <p className="text-sm mt-1" style={{ color: "var(--text-muted)" }}>{t("otherCrops.emptyHint")}</p>
          </div>
        ) : (
          <section className="vs-card p-4 sm:p-5" data-testid="other-crops-table-section">
            <div className="hidden md:block overflow-x-auto other-crops-table-wrap">
              <table className="w-full text-sm other-crops-table">
                <thead>
                  <tr style={{ background: "var(--secondary)" }}>
                    <th style={thStyle}>{t("otherCrops.table.date")}</th>
                    <th style={thStyle}>{t("otherCrops.table.crop")}</th>
                    <th style={thStyle}>{t("otherCrops.table.product")}</th>
                    <th style={thStyle}>{t("otherCrops.table.quantity")}</th>
                    <th style={thStyle}>{t("otherCrops.table.surface")}<br /><span className="font-normal">{t("otherCrops.table.surfaceSub")}</span></th>
                    <th style={thStyle}>{t("otherCrops.table.disease")}<br />{t("otherCrops.table.diseaseSub")}</th>
                    <th style={thStyle}>{t("otherCrops.table.zone")}</th>
                    <th style={thStyle}>{t("otherCrops.table.operator")}<br />{t("otherCrops.table.operatorSub")}</th>
                    <th style={thStyle}>{t("otherCrops.table.signature")}</th>
                    <th style={{ ...thStyle, width: 90 }} className="print-hide">{t("otherCrops.table.sds")}</th>
                    <th style={{ ...thStyle, width: 64 }} className="print-hide">{t("otherCrops.table.actions")}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map((row, idx) => {
                    const prev = idx > 0 ? sorted[idx - 1] : null;
                    const isNewDate = !prev || new Date(prev.treatment_date).toDateString() !== new Date(row.treatment_date).toDateString();
                    return (
                      <Fragment key={row.id}>
                        {idx > 0 && isNewDate && (
                          <tr aria-hidden="true" className="date-sep-row print-hide">
                            <td colSpan={11} />
                          </tr>
                        )}
                        <tr data-testid={`other-crops-row-${row.id}`} className="other-crops-row">
                          <td style={tdStyle}>{fmtDate(row.treatment_date, dateLocale)}</td>
                          <td style={tdStyle}><span className="font-bold">{row.crop_name}</span></td>
                          <td style={tdStyle}>{row.product_name}</td>
                          <td style={tdStyle}>{row.quantity} {row.quantity_unit}</td>
                          <td style={tdStyle}>{row.surface_treated != null ? row.surface_treated : "—"}</td>
                          <td style={tdStyle}>{row.target_disease || "—"}</td>
                          <td style={tdStyle}>{row.zone || "—"}</td>
                          <td style={tdStyle}>{row.operator_name || "—"}</td>
                          <td style={{ ...tdStyle, minWidth: 90 }}></td>
                          <td style={{ ...tdStyle, padding: "6px" }} className="print-hide">
                            <SdsButton t={t} productName={row.product_name} testId={`sds-btn-${row.id}`} />
                          </td>
                          <td style={{ ...tdStyle, padding: "6px" }} className="print-hide">
                            <ActionButtons onEdit={() => openEdit(row)} onDelete={() => handleDelete(row.id)} />
                          </td>
                        </tr>
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="md:hidden space-y-3">
              {sorted.map((row, idx) => {
                const prev = idx > 0 ? sorted[idx - 1] : null;
                const isNewDate = !prev || new Date(prev.treatment_date).toDateString() !== new Date(row.treatment_date).toDateString();
                return (
                  <Fragment key={row.id}>
                    {idx > 0 && isNewDate && (
                      <div aria-hidden="true" style={{ height: 4, background: "var(--primary)", borderRadius: 2, margin: "8px 0" }} />
                    )}
                    <div className="p-3 rounded-lg"
                      style={{ border: "2px solid var(--border)", background: "var(--surface)" }}
                      data-testid={`other-crops-card-${row.id}`}>
                      <div className="flex items-start justify-between mb-2 pb-2" style={{ borderBottom: "1px solid var(--border)" }}>
                        <div>
                          <div className="heading font-extrabold text-base">{row.crop_name}</div>
                          <div className="text-xs" style={{ color: "var(--text-muted)" }}>{row.product_name}</div>
                        </div>
                        <div className="flex flex-col items-end gap-2">
                          <div className="text-xs font-bold" style={{ color: "var(--primary)" }}>{fmtDate(row.treatment_date, dateLocale)}</div>
                          <ActionButtons onEdit={() => openEdit(row)} onDelete={() => handleDelete(row.id)} />
                        </div>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs">
                        <KV label={t("otherCrops.table.quantity")} value={`${row.quantity} ${row.quantity_unit}`} />
                        <KV label={t("otherCrops.table.surface")} value={row.surface_treated != null ? row.surface_treated : "—"} />
                        <KV label={t("otherCrops.table.disease")} value={row.target_disease || "—"} />
                        <KV label={t("otherCrops.table.zone")} value={row.zone || "—"} />
                        <KV label={t("otherCrops.table.operator")} value={row.operator_name || "—"} />
                      </div>
                      {row.notes && (
                        <div className="text-xs italic mt-2 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px dashed var(--border)" }}>
                          {row.notes}
                        </div>
                      )}
                      <div className="mt-2">
                        <SdsButton t={t} productName={row.product_name} testId={`sds-btn-mobile-${row.id}`} />
                      </div>
                    </div>
                  </Fragment>
                );
              })}
            </div>

            <div className="text-xs mt-4 pt-2" style={{ color: "var(--text-muted)", borderTop: "1px solid var(--border)" }}>
              {t("otherCrops.rowCount", { count: sorted.length })}
            </div>
          </section>
        )}

        <div className="vs-card p-4 mt-5" data-testid="other-crops-disclaimer"
          style={{ background: "#fef3c7", border: "2px solid #f59e0b", borderLeft: "6px solid #f59e0b" }}>
          <div className="flex items-start gap-3">
            <AlertTriangle size={20} style={{ color: "#92400e", flexShrink: 0, marginTop: 2 }} />
            <div className="text-sm" style={{ color: "#7c2d12" }}>
              <p className="font-semibold">{t("otherCrops.authDisclaimer")}</p>
              <p className="mt-1 italic text-xs">{t("catalogo.sdsDisclaimer")}</p>
            </div>
          </div>
        </div>
      </main>
      <BottomNav />

      {editItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 print-hide"
          style={{ background: "rgba(0,0,0,0.55)" }}
          onClick={(e) => { if (e.target === e.currentTarget) closeEdit(); }}>
          <div className="vs-card w-full max-w-lg p-5 max-h-[90vh] overflow-y-auto" style={{ border: "2px solid var(--primary)" }}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="heading font-extrabold text-lg">{t("otherCrops.editTitle")}</h3>
              <button onClick={closeEdit} className="p-1"><X size={20} /></button>
            </div>
            <div className="space-y-3 text-sm">
              <FormField label={t("otherCrops.fields.date")} type="date" value={editForm.treatment_date}
                onChange={(v) => setEditForm((f) => ({ ...f, treatment_date: v }))} />
              <FormField label={t("otherCrops.fields.crop")} value={editForm.crop_name}
                onChange={(v) => setEditForm((f) => ({ ...f, crop_name: v }))} />
              <FormField label={t("otherCrops.fields.product")} value={editForm.product_name}
                onChange={(v) => setEditForm((f) => ({ ...f, product_name: v }))} />
              <FormField label={t("otherCrops.fields.quantity")} type="number" value={editForm.quantity}
                onChange={(v) => setEditForm((f) => ({ ...f, quantity: v }))} />
              <FormField label={t("otherCrops.fields.quantityUnit")} value={editForm.quantity_unit}
                onChange={(v) => setEditForm((f) => ({ ...f, quantity_unit: v }))} />
              <FormField label={t("otherCrops.fields.zone")} value={editForm.zone}
                onChange={(v) => setEditForm((f) => ({ ...f, zone: v }))} />
              <FormField label={t("otherCrops.fields.surface")} type="number" value={editForm.surface_treated}
                onChange={(v) => setEditForm((f) => ({ ...f, surface_treated: v }))} />
              <FormField label={t("otherCrops.fields.disease")} value={editForm.target_disease}
                onChange={(v) => setEditForm((f) => ({ ...f, target_disease: v }))} />
              <FormField label={t("otherCrops.fields.operator")} value={editForm.operator_name}
                onChange={(v) => setEditForm((f) => ({ ...f, operator_name: v }))} />
              <FormField label={t("otherCrops.fields.notes")} value={editForm.notes} textarea
                onChange={(v) => setEditForm((f) => ({ ...f, notes: v }))} />
            </div>
            <div className="flex gap-3 mt-5">
              <button onClick={closeEdit}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm"
                style={{ background: "var(--surface)", border: "2px solid var(--border)", color: "var(--text-primary)" }}>
                {t("otherCrops.cancelButton")}
              </button>
              <button onClick={saveEdit} disabled={saving}
                className="flex-1 py-2.5 rounded-xl font-bold text-sm inline-flex items-center justify-center gap-2"
                style={{ background: "var(--gradient-vine)", color: "white", border: "2px solid var(--primary)", opacity: saving ? 0.7 : 1 }}>
                <Check size={16} /> {saving ? t("otherCrops.saving") : t("otherCrops.saveEditButton")}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`
        .other-crops-table { border: 2px solid var(--text-primary); border-collapse: collapse; }
        .other-crops-row:hover td { background: rgba(0,0,0,0.025); }
        .date-sep-row td { height: 6px; padding: 0; background: var(--primary); border: none; }
        @media print {
          @page { size: A4 landscape; margin: 12mm 10mm; }
          html, body { background: white !important; }
          .print-hide, nav, header, .vs-bottom-nav { display: none !important; }
          main { padding: 0 !important; max-width: none !important; }
          .vs-card { border: 1px solid #000 !important; box-shadow: none !important; break-inside: avoid; padding: 8mm !important; margin-bottom: 6mm !important; background: white !important; }
          .heading { color: #000 !important; }
          .other-crops-table-wrap { display: block !important; }
          .other-crops-table-wrap table { font-size: 11pt !important; }
          .other-crops-table-wrap thead tr { background: #f1ede2 !important; -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .other-crops-table-wrap th, .other-crops-table-wrap td { border: 1px solid #000 !important; color: #000 !important; padding: 6px 8px !important; }
          .md\\:hidden { display: none !important; }
          .hidden.md\\:block { display: block !important; }
        }
      `}</style>
    </div>
  );
}

const thStyle = {
  border: "1.5px solid var(--text-primary)",
  padding: "8px 10px",
  textAlign: "center",
  fontSize: "12px",
  fontWeight: 700,
  lineHeight: 1.2,
};

const tdStyle = {
  border: "1px solid var(--text-primary)",
  padding: "8px 10px",
  textAlign: "center",
  fontSize: "13px",
  verticalAlign: "middle",
};

function FormField({ label, value, onChange, type = "text", textarea, placeholder, required }) {
  const cls = "vs-input w-full mt-1";
  return (
    <div>
      <label className="text-xs font-bold uppercase tracking-wider" style={{ color: "var(--text-muted)" }}>
        {label}{required && " *"}
      </label>
      {textarea ? (
        <textarea className={cls} rows={2} value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} />
      ) : (
        <input className={cls} type={type} value={value} placeholder={placeholder} required={required} onChange={(e) => onChange(e.target.value)} />
      )}
    </div>
  );
}

function KV({ label, value }) {
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold" style={{ color: "var(--text-muted)" }}>{label}</div>
      <div className="font-semibold text-sm">{value}</div>
    </div>
  );
}

function SdsButton({ t, productName, testId }) {
  return (
    <button onClick={() => cercaSDS(productName)}
      className="inline-flex items-center gap-1 text-xs px-2 py-1.5 rounded-lg font-bold"
      style={{ background: "white", color: "#166534", border: "2px solid #22c55e" }}
      title={t("catalogo.searchSdsButton")} data-testid={testId}>
      <ExternalLink size={13} /> {t("otherCrops.table.sds")}
    </button>
  );
}

const ACTION_BTN_BASE = {
  width: 32, height: 32, borderRadius: 6,
  display: "inline-flex", alignItems: "center", justifyContent: "center",
  border: "none", cursor: "pointer",
  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
  transition: "transform 0.15s",
};

function ActionButtons({ onEdit, onDelete }) {
  const { t } = useTranslation();
  return (
    <div className="flex flex-col items-center gap-1.5 print-hide">
      <button onClick={onEdit} aria-label={t("dashboard.card.editButton")} data-testid="other-crops-edit-btn"
        style={{ ...ACTION_BTN_BASE, background: "var(--primary)" }}>
        <Pencil size={16} color="white" strokeWidth={2.5} />
      </button>
      <button onClick={onDelete} aria-label={t("dashboard.card.deleteAriaLabel")} data-testid="other-crops-delete-btn"
        style={{ ...ACTION_BTN_BASE, background: "#dc2626" }}>
        <Trash2 size={16} color="white" strokeWidth={2.5} />
      </button>
    </div>
  );
}
